import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Box, Button, Grid, Typography } from "@mui/material";

import { ReactComponent as FilterIcon } from "src/assets/icon/filter.svg";
import { ReactComponent as SearchIcon } from "src/assets/icon/search.svg";
import FilterMenu from "src/components/filter/FilterMenu";
import NewTextField from "src/components/form/TextField";
import useLocales from "src/hooks/useLocales";

export interface IPurchaseOrderFilterState {
  poNumber?: string;
  vendorCompanyName?: string;
  customerCompanyName?: string;
}

interface IPurchaseOrderFiltersProps {
  loader: boolean;
  filterState: IPurchaseOrderFilterState;
  setFilterState: React.Dispatch<
    React.SetStateAction<IPurchaseOrderFilterState>
  >;
}

export const PurchaseOrderDraftFilters: React.FC<IPurchaseOrderFiltersProps> = ({
  loader,
  filterState,
  setFilterState,
}) => {
  const { translate } = useLocales();
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [initialValues, setInitialValues] =
    useState<IPurchaseOrderFilterState>(filterState);

  useEffect(() => {
    setInitialValues(filterState);
  }, [filterState]);

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOnDelete = (key: string) => {
    setFilters((prev) => {
      const f = { ...prev };
      delete f[key];
      return f;
    });

    setInitialValues((prev) => ({ ...prev, [key]: undefined as any }));
    setFilterState((prev) => ({
      ...prev,
      [key]: undefined as any,
    }));
  };

  const filterSettersMap = {
    poNumber: (value: string) =>
      value
        ? {
            name: "poNumber",
            value,
            displayValue: value,
            label: translate("purchase_order_id"),
          }
        : undefined,
    vendorCompanyName: (value: string) =>
      value
        ? {
            name: "vendorCompanyName",
            value,
            displayValue: value,
            label: translate("vendor_company_name"),
          }
        : undefined,
    customerCompanyName: (value: string) =>
      value
        ? {
            name: "customerCompanyName",
            value,
            displayValue: value,
            label: translate("customer_company_name"),
          }
        : undefined,
  };

  const handleFilters = (v: IPurchaseOrderFilterState) => {
    const newFilters: Record<string, any> = {};

    Object.entries(v).forEach(([k, val]) => {
      if (
        val !== undefined &&
        filterSettersMap[k as keyof typeof filterSettersMap]
      ) {
        const filterFn = filterSettersMap[
          k as keyof typeof filterSettersMap
        ] as any;
        const filterObj = filterFn(val);
        if (filterObj) newFilters[k] = filterObj;
      }
    });

    setFilters(newFilters);
  };

  const handleChange = (name: string, value: any) =>
    setInitialValues((prev) => ({ ...prev, [name]: value }));

  const { handleSubmit, setValue } = useForm();

  return (
    <FilterMenu
      filters={filters}
      inputRef={inputRef}
      anchorEl={anchorEl}
      open={open}
      handleOpenMenu={handleOpenMenu}
      handleCloseMenu={handleCloseMenu}
      handleOnDelete={handleOnDelete}
      loader={loader}
    >
      <form
        onSubmit={handleSubmit(() => {
          setFilterState((prev) => ({
            ...prev,
            ...initialValues,
          }));

          handleFilters(initialValues);
          handleCloseMenu();
        })}
      >
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12}>
            <Typography variant="h6">
              <FilterIcon height={18} width={18} /> {translate("Filters")}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <NewTextField
              onChange={(e) => {
                handleChange("poNumber", e.target.value);
                setValue("poNumber", e.target.value);
              }}
              value={initialValues.poNumber ?? ""}
              label={translate("purchase_order_id")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <NewTextField
              onChange={(e) => {
                handleChange("vendorCompanyName", e.target.value);
                setValue("vendorCompanyName", e.target.value);
              }}
              value={initialValues.vendorCompanyName ?? ""}
              label={translate("vendor_company_name")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <NewTextField
              onChange={(e) => {
                handleChange("customerCompanyName", e.target.value);
                setValue("customerCompanyName", e.target.value);
              }}
              value={initialValues.customerCompanyName ?? ""}
              label={translate("customer_company_name")}
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SearchIcon stroke="white" height={20} width={20} />}
            type="submit"
          >
            {translate("finance_screen.search")}
          </Button>
        </Box>
      </form>
    </FilterMenu>
  );
};
